import React from 'react';

interface Column {
    header: string;
    key: string;
    render?: (item: any) => React.ReactNode;
}

interface DetailPopupProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    data?: any[];
    columns?: Column[];
    isLoading?: boolean;
    children?: React.ReactNode;
}

export default function DetailPopup({
    isOpen,
    onClose,
    title,
    data = [],
    columns = [],
    isLoading = false,
    children,
}: DetailPopupProps) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 transition-opacity">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full mx-4 animate-in fade-in zoom-in duration-200 overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                        <div className="w-10 h-10 flex items-center justify-center bg-teal-100 dark:bg-teal-900/30 rounded-full mr-3">
                            <i className="ri-list-check text-teal-600 dark:text-teal-400 text-xl"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        aria-label="Close"
                    >
                        <i className="ri-close-line text-2xl"></i>
                    </button>
                </div>

                {/* Body */}
                <div className={`overflow-y-auto flex-1 ${children ? 'p-6' : 'p-0'}`}>
                    {children ? (
                        children
                    ) : isLoading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
                            <p className="mt-4 text-gray-500 dark:text-gray-400">Loading details...</p>
                        </div>
                    ) : data.length > 0 ? (
                        <div className="min-w-full inline-block align-middle">
                            <div className="border-b border-gray-200 dark:border-gray-700">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                                        <tr>
                                            {columns.map((col, idx) => (
                                                <th
                                                    key={idx}
                                                    scope="col"
                                                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                                >
                                                    {col.header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {data.map((item, rowIdx) => (
                                            <tr key={rowIdx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                {columns.map((col, colIdx) => (
                                                    <td key={colIdx} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                        {col.render ? col.render(item) : item[col.key]}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                            <i className="ri-inbox-line text-4xl mb-2"></i>
                            <p>No data available to display.</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-semibold transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
